/**
 * The purchase register, gathered by supplier.
 *
 * A register states one supplier per line, so a supplier with five invoices is
 * five rows and a reviewer asking "is P G TRONICS right?" reassembles it by
 * eye. This gathers those rows under one heading — in the SCREEN only. It
 * changes no value, decides no verdict, and posts nothing; the rows it returns
 * are the same objects it was given.
 *
 * What it must not do, which is the whole of the design:
 *
 *   - It must not group by name. One trading name can front two registrations,
 *     and merging them shows a supplier total belonging to no GSTIN.
 *   - It must not group rows whose GSTIN is missing. Those are the rows most
 *     in need of being told apart, because the absent GSTIN is the thing under
 *     review — see `keyOf`.
 *   - It must not reorder beyond gathering. Order arrives from the service and
 *     a grouping that sorts is a sort wearing a grouping's clothes.
 */

import type { ReviewRow } from './api'

/**
 * The separator inside a composite key.
 *
 * A null byte, NOT a pipe or a dash. Supplier names in a register are typed by
 * hand and do contain both, and with a printable separator `"AB" + "C"` and
 * `"A" + "BC"` produce the same key — two suppliers silently merged, which is
 * exactly the failure this key exists to prevent. A null byte cannot occur in
 * either field, so the concatenation is injective.
 */
const SEP = ' '

/** A row's field as trimmed text, or null when it says nothing. */
function text(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * Whose rows a register lists (N30). A purchase register names a supplier per line, a sales
 * register a buyer. The screen grouped every register by supplier, so a sales register became
 * one heading per row, each "— GSTIN: —": a sale carries no supplier at all.
 */
export type Party = 'supplier' | 'buyer'

/** The party a document's rows name, from its served direction; null for anything else. */
export function partyOf(direction: string | null | undefined): Party | null {
  if (direction === 'purchase') return 'supplier'
  if (direction === 'sales') return 'buyer'
  return null
}

const FIELDS: Record<Party, { name: string; gstin: string }> = {
  supplier: { name: 'supplier_name', gstin: 'gstin_supplier' },
  buyer: { name: 'buyer_name', gstin: 'gstin_buyer' },
}

/**
 * The one heading that gathers rows sharing an absence (owner, 2026-10-23, option A): sales
 * to buyers with no GSTIN. It names a KIND of sale — unregistered, B2C — and never a party, so
 * it claims nothing about who bought; a sales register is mostly such rows, and a heading each
 * doubled the page. A purchase row with no supplier GSTIN is still never gathered: there the
 * absence is the thing under review.
 */
export const UNREGISTERED_KEY = 'unregistered-buyers'

export type SupplierGroup = {
  /** Stable across renders for the same page of rows; a React key. */
  key: string
  /** What the rows say, or null when the register never said. */
  supplierName: string | null
  gstin: string | null
  /** True for the one heading of sales to buyers with no GSTIN. */
  unregistered?: boolean
  /** The member rows, in the order the service sent them. */
  rows: ReviewRow[]
  /**
   * Sum of `taxable` over THIS PAGE's members, in integer paise. Never a
   * whole-document figure — the service pages rows at 200, and a supplier can
   * straddle a page boundary. The screen labels it as page-scoped.
   */
  totalTaxable: number
}

/**
 * The grouping key, or null when this row may group with nothing.
 *
 * Null is returned when either half is missing, and the caller then keys the
 * row off its own identity. That is the requirement that reads oddly and
 * matters most: two rows that BOTH lack a supplier GSTIN are not thereby the
 * same supplier. Treating a shared absence as a shared identity would put two
 * unrelated taxpayers' invoices under one heading and one total, at precisely
 * the moment a reviewer is looking for the missing registration.
 */
function keyOf(row: ReviewRow, party: Party): string | null {
  const name = text(row.effective[FIELDS[party].name])
  const gstin = text(row.effective[FIELDS[party].gstin])
  if (party === 'buyer' && gstin === null) return UNREGISTERED_KEY
  if (name === null || gstin === null) return null
  return `${name}${SEP}${gstin}`
}

/**
 * Gather a page of rows by supplier name AND supplier GSTIN.
 *
 * Groups come back in first-appearance order, and rows keep their order inside
 * a group, so whatever order the service chose survives: switching the screen's
 * grouping off restores the original sequence exactly, because `rows` is read
 * and never mutated.
 */
export function groupBySupplier(rows: readonly ReviewRow[]): SupplierGroup[] {
  return groupByParty(rows, 'supplier')
}

/** The same gathering, by whichever party the register names (N30). */
export function groupByParty(rows: readonly ReviewRow[], party: Party): SupplierGroup[] {
  const groups: SupplierGroup[] = []
  const byKey = new Map<string, SupplierGroup>()

  for (const row of rows) {
    const shared = keyOf(row, party)
    // A row that may group with nothing is keyed by its own content key, so it
    // is a group of one. `ungrouped:` is prefixed rather than used bare because
    // a content key is opaque and could in principle collide with a composite
    // key; the prefix cannot appear in one, since a composite always carries a
    // null byte and this never does.
    const key = shared ?? `ungrouped:${row.content_key}`

    const taxable = row.effective.taxable
    // A non-numeric taxable contributes nothing rather than NaN. One unparsed
    // cell must not turn a supplier's whole total into "NaN" on screen.
    const paise = typeof taxable === 'number' && Number.isFinite(taxable) ? taxable : 0

    const found = byKey.get(key)
    if (found) {
      found.rows.push(row)
      found.totalTaxable += paise
      continue
    }

    const unregistered = key === UNREGISTERED_KEY
    const group: SupplierGroup = {
      key,
      // The category heading takes no party's name: it is not one party.
      supplierName: unregistered ? null : text(row.effective[FIELDS[party].name]),
      gstin: unregistered ? null : text(row.effective[FIELDS[party].gstin]),
      ...(unregistered ? { unregistered: true } : {}),
      rows: [row],
      totalTaxable: paise,
    }
    byKey.set(key, group)
    groups.push(group)
  }

  return groups
}

/**
 * A group's heading in words (N30): never a bare dash. A missing half is said, because on a
 * purchase register a missing supplier GSTIN is exactly what the reviewer is looking for.
 */
export function groupHeading(group: SupplierGroup): { name: string; gstin: string | null } {
  if (group.unregistered) return { name: 'Unregistered buyers (no GSTIN)', gstin: null }
  return {
    name: group.supplierName ?? 'Name not stated',
    gstin: group.gstin ?? 'GSTIN not stated',
  }
}

/** The toggle and the count, naming the party the register lists. */
export function groupingWords(party: Party, count: number): { on: string; off: string; count: string } {
  const one = party === 'buyer' ? 'buyer' : 'supplier'
  return {
    on: `Grouped by ${one}`,
    off: `Group by ${one}`,
    count: count === 1 ? `1 ${one} on this page` : `${count.toLocaleString()} ${one}s on this page`,
  }
}
